<template>
  <wui-card
    class="w-[280px] phone:w-[240px]"
    state="text"
    shadow="overflow"
    data-test="chat-participant-context-menu"
    @click.stop
  >
    <wui-list>
      <wui-list-item
        v-if="canOpenProfile"
        icon="userM"
        data-test="chat-participant-menu-open-profile"
        @click="onOpenProfile"
      >
        <p>{{ $t('chat.openProfile') }}</p>
      </wui-list-item>

      <hr
        v-if="canOpenProfile && (canSendMessage || canCall)"
        class="mx-1 my-1 border-t-bor-nav"
      >

      <wui-list-item
        v-if="canSendMessage"
        icon="messageM"
        data-test="chat-participant-menu-send-message"
        @click="onSendMessage"
      >
        <p>{{ $t('chat.sendMessage') }}</p>
      </wui-list-item>

      <wui-list-item
        v-if="canCall"
        :icon="isCalling ? 'spinner' : 'phoneM'"
        :disabled="isCalling"
        data-test="chat-participant-menu-call"
        @click="onCall"
      >
        <p>{{ $t('common.makeCall') }}</p>
      </wui-list-item>

      <template v-if="canManageModeratorRole">
        <hr class="mx-1 my-1 border-t-bor-nav">
        <wui-list-item
          v-if="contactRole === 'User'"
          icon="moderatorM"
          :disabled="isRoleChanging"
          data-test="chat-participant-menu-make-moderator"
          @click="onChangeRole('Moderator')"
        >
          <p>{{ $t('chat.assignModerator') }}</p>
        </wui-list-item>
        <wui-list-item
          v-else-if="contactRole === 'Moderator'"
          icon="moderatorM"
          :disabled="isRoleChanging"
          data-test="chat-participant-menu-revoke-moderator"
          @click="onChangeRole('User')"
        >
          <p>{{ $t('chat.revokeModerator') }}</p>
        </wui-list-item>
      </template>

      <template v-if="canRemove">
        <hr class="mx-1 my-1 border-t-bor-nav">
        <wui-list-item
          icon="clear"
          variant="negative"
          :disabled="isRemoving"
          data-test="chat-participant-menu-remove"
          @click="onRemove"
        >
          <p>{{ $t('chat.removeFromGroup') }}</p>
        </wui-list-item>
      </template>
    </wui-list>
  </wui-card>
</template>

<script setup lang="ts">
import {
  useRenderNotifier,
  WuiCard,
  WuiList,
  WuiListItem,
} from '@wui/common-library'
import { ConferenceMemberRole, ConferenceType, isBotPServed } from '@wui/im'
import { computed, ref, toRef } from 'vue'
import { useRouter } from 'vue-router'

import { useStoreRooms, useModeratedRoom } from '@/entities/room'
import { useRoom } from '@/entities/use-room'

import { useConfirmDialog } from '@/shared/components/dialogs'
import { useLocalization } from '@/shared/composables/localization'
import { openParticipantProfileOverlay } from '@/shared/composables/use-participant-profile-overlay'
import { useRoomControl } from '@/shared/composables/use-room-control'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { PSTNGUEST } from '@/shared/utils/constants'
import { fromPServedUser } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

const props = defineProps<{
  room: Room
  contact: Contact
  isRemovable?: boolean
}>()

const emit = defineEmits<{
  (event: 'hideMenu'): void
}>()

const router = useRouter()
const storeRooms = useStoreRooms()
const { removeMember, changeSubscriberRole } = useRoom()
const { memberCall } = useRoomControl()
const { notifyError, notifySuccess, notifyWarning } = useRenderNotifier()
const { t } = useLocalization()
const { show: showConfirmDialog } = useConfirmDialog()
const roomRef = toRef(props, 'room')
const { canIEditRoomInfo, getSubscriberRole } = useModeratedRoom(roomRef)

const isRemoving = ref(false)
const isRoleChanging = ref(false)
const isCalling = ref(false)

const isBot = computed(() => Boolean(props.contact.imLogin && isBotPServed(props.contact.imLogin)))
const isExternalContact = computed(() => {
  return props.contact.type === 'pstnGuest'
    || props.contact.type === 'guest'
    || !props.contact.imLogin
})

const contactRole = computed(() => {
  if (!props.contact.imLogin) {
    return undefined
  }
  return getSubscriberRole(props.contact.imLogin)
})

const isModerated = computed(() => props.room.mode === ConferenceType.MODERATED)

const canOpenProfile = computed(() => !isExternalContact.value && !isBot.value)
const canSendMessage = computed(() => Boolean(props.contact.imLogin) && !isBot.value)
const canCall = computed(() => !isBot.value && Boolean(props.contact.imLogin || props.contact.mobilePhone))

const canManageModeratorRole = computed(() => {
  return isModerated.value
    && canIEditRoomInfo.value
    && !isExternalContact.value
    && (contactRole.value === 'User' || contactRole.value === 'Moderator')
})

const canRemove = computed(() => Boolean(props.isRemovable))

const targetRoom = computed(() => storeRooms.getRoomById(fromPServedUser(props.room.id)))

const onOpenProfile = () => {
  openParticipantProfileOverlay(props.contact)
  emit('hideMenu')
}

const onSendMessage = async () => {
  const chatRoute = getChatRoute(props.contact)
  if (chatRoute) {
    await router.push(chatRoute)
  }
  emit('hideMenu')
}

const onCall = async () => {
  if (isCalling.value) {
    return
  }

  let number = ''
  if (isExternalContact.value) {
    number = `${PSTNGUEST}-`
    if (props.contact.internalNumber) {
      number += props.contact.internalNumber
    } else if (props.contact.mobilePhone) {
      number += props.contact.mobilePhone
    }
  } else {
    number = props.contact.internalNumber
  }

  if (!number || number === `${PSTNGUEST}-`) {
    return
  }

  isCalling.value = true
  try {
    await memberCall(props.room.pServedUser, number)
    emit('hideMenu')
  } catch (e: unknown) {
    notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  } finally {
    isCalling.value = false
  }
}

const onChangeRole = async (role: Exclude<ConferenceMemberRole, 'Owner' | 'External'>) => {
  if (!props.contact.internalNumber || isRoleChanging.value) {
    return
  }

  emit('hideMenu')

  if (role === 'Moderator') {
    const confirmed = await showConfirmDialog({
      title: t('chat.assignModeratorTitle'),
      message: t('chat.assignModeratorConfirm', {
        name: props.contact.name || t('common.noName'),
      }),
      confirmLabel: t('chat.assign'),
      cancelLabel: t('common.cancel'),
    })
    if (!confirmed) {
      return
    }
  }

  isRoleChanging.value = true
  try {
    await changeSubscriberRole(props.room.pServedUser, props.contact.internalNumber, role)
    notifySuccess(t('chat.moderatorsHasBeenChanged'))
  } catch (e) {
    console.error(e)
  } finally {
    isRoleChanging.value = false
  }
}

const onRemove = async () => {
  if (isRemoving.value || !targetRoom.value) {
    return
  }

  let number: string
  if (!props.contact.internalNumber && props.contact.mobilePhone) {
    number = `${PSTNGUEST}-${props.contact.mobilePhone}`
  } else if (isExternalContact.value) {
    number = `${PSTNGUEST}-${props.contact.internalNumber}`
  } else {
    number = props.contact.internalNumber
  }

  if (!number) {
    return
  }

  isRemoving.value = true
  try {
    await removeMember(targetRoom.value, number)
    storeRooms.removeRoomMembers({
      roomId: fromPServedUser(props.room.id),
      members: [number],
    })
    notifySuccess(t('common.userNameHasBeenRemovedFromGroup', { name: props.contact.name }))
    emit('hideMenu')
  } catch {
    notifyWarning(t('common.youCannotDeleteAParticipantDuringACall', { name: props.contact.name }))
    notifyError(t('common.failedToRemoveUserNameFromRoom', { name: props.contact.name }))
  } finally {
    isRemoving.value = false
  }
}
</script>
